/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { Ref } from 'react';

import { Dialog } from '@connected/ui/components/dialog';
import { Skeleton } from '@connected/ui/components/skeleton';
import { cn } from '@connected/ui/lib/utils';
import { ImageOff } from 'lucide-react';
import { useEffect, useState } from 'react';

export type ImageProps = ImageExpandableProps & {
    file: File | null;
    disabled?: boolean;
    className?: string;
    alt?: string;
    isLoading?: boolean;
};

export type ImageExpandableProps = ({
    expandable: true;
    ref?: Ref<HTMLButtonElement>;
} | {
    expandable?: false;
    ref?: Ref<HTMLImageElement>;
});

export function Image({ file, disabled, expandable, className, alt, ref, isLoading }: ImageProps) {
    const [src, setSrc] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (disabled || !file || !file?.type?.startsWith('image/')) {
            setSrc(null);

            return;
        }

        const objectUrl = URL.createObjectURL(file);

        setSrc(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [disabled, file]);

    // Only show loading skeleton if we don't have a file yet
    // If we have a file but isLoading is true (during refetch), keep showing the existing file
    if (isLoading && !file) {
        return (
            <Skeleton className={cn('w-full h-full object-contain flex items-center justify-center bg-background-tertiary', className)}>
                <ImageOff size={24} />
            </Skeleton>
        );
    }

    if (!src || disabled) {
        return (
            <div className={cn('w-full h-full object-contain flex items-center justify-center bg-background-tertiary', className)}>
                <ImageOff size={24} />
            </div>
        );
    }

    if (expandable !== true) {
        return (
            <img
                src={src}
                className={cn('w-full h-full object-contain pointer-events-none', className)}
                draggable={false}
                alt={alt}
                ref={ref}
            />
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Trigger asChild>
                <button
                    type="button"
                    className={cn('w-full h-full bg-cover bg-center', className)}
                    draggable={false}
                    style={{ backgroundImage: `url(${src})` }}
                    ref={ref as Ref<HTMLButtonElement>}
                />
            </Dialog.Trigger>
            <Dialog.Content
                className="max-w-none max-h-none w-full h-full flex items-center justify-center p-0 bg-transparent shadow-none border-0 cursor-zoom-out"
                onClick={() => setIsOpen(false)}
            >
                <img
                    src={src}
                    className="max-w-[min(95vw,1400px)] max-h-[95vh] rounded-md shadow-2xl bg-white"
                    draggable={false}
                    alt={alt}
                />
            </Dialog.Content>
        </Dialog>
    );
}
